import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Divider, Stack, useTheme } from "@mui/material";
import { QueryInfo, QueryField, socialUniverseField } from "system/query/QueryInfo";
import { SimpleFormLabel } from "components/dialogs/SimpleFormDialog";
import { Agg } from "system/query/Aggs";
import LeafAggSelector from "components/widgetParts/LeafAggSelector";
import TabbedDialog from "components/dialogs/TabbedDialog";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SettingsWidgetTitleSelect from "components/settingsConfig/uiComponents/SettingsWidgetTitleSingleSelect";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import SettingsTextField from "components/settingsConfig/uiComponents/SettingsTextField";
import SettingsSingleSelect from "components/settingsConfig/uiComponents/SettingsSingleSelect";
import SettingsToggleButton, { horizontalAlignmentOptions } from "components/settingsConfig/uiComponents/SettingsToggleButton";
import { AutoWidgetQueryBehavior,getSingleOriginalRequestByName,getSingleWidgetResponseByName,WidgetRequest } from "system/widget/Widget";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import ShareOfVoicePreview from "./ShareOfVoicePreview";
import { i18n } from "i18next";

const defaultWidgetRequest = {
  name: 'share-of-voice',
  endpoint: 'query/agg',
  queryParams: {
    flatten: true,
    include_total: true
  },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_MULTI_ALWAYS
};

export function BuildDefaultShareOfVoiceRequests(queryInfo: QueryInfo): WidgetRequest[] | undefined {
  const foundSocialUniverseField = queryInfo.fields?.find((field: QueryField) => field.id === socialUniverseField.id) || false;

  const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };

  const requestInitBody = { aggs: {} };
  if (foundSocialUniverseField) {
    requestInitBody.aggs = {
      [socialUniverseField.label]: {
        op: socialUniverseField.id
      }
    };
  }
  widgetRequest.requestInit = { body: requestInitBody };
  return [widgetRequest];
}

export type ShareOfVoiceSettingsStateType = {
  customWidgetTitle: string;
  widgetTitle: string;
  widgetTitleAlignment: string;
  sortValue: "none" | "asc" | "desc";
  displayValue: "none" | "percent" | "count";
}

export const defaultSettings: ShareOfVoiceSettingsStateType = {
  customWidgetTitle: '',
  widgetTitle: 'default',
  widgetTitleAlignment: 'left',
  sortValue: 'desc',
  displayValue: 'percent',
}

export function getDisplayValueOptions(i18n: i18n) {
  return [
    { label: i18n.t("none"), value: "none" },
    { label: i18n.t("percent"), value: "percent" },
    { label: i18n.t("count"), value: "count" },
  ]
}

export function getCategorySortingOptions(i18n: i18n) {
  return [
    { label: i18n.t("widgets.settings.sorting.options.original-query-order"), value: "none" },
    { label: i18n.t("widgets.settings.sorting.options.value-asc"), value: "asc" },
    { label: i18n.t("widgets.settings.sorting.options.value-desc"), value: "desc" },
  ]
}

export default function ShareOfVoiceSettings(props: WidgetSettingsProps): JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const apiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'share-of-voice');
  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'share-of-voice');
  const requestBody = originalRequest?.requestInit?.body || {};

  const keys = Object.keys(requestBody?.aggs || {});
  let firstAggKey = "";
  let aggDefinition = null;
  if (keys.length) {
    firstAggKey = keys[0];
    aggDefinition = requestBody.aggs[firstAggKey];
  }

  const [aggsLabel, setAggsLabel] = useState<string>(firstAggKey);
  const [localAggs, setLocalAggs] = useState<Agg>(aggDefinition);

  // check if the data object has the fields needed
  const updateAggs = () => {
    const label:string = aggsLabel || localAggs?.field || "total";

    const newRequestInfo = localAggs ? {body: {aggs: { [label]: localAggs }}} : {body: {aggs: {}}};

    if (props.widget?.id && props.onUpdateWidgetData) {
      props.onUpdateWidgetData(props.widget?.id, {
        request_info: newRequestInfo,
        settings: settingsState,
      }).then(() => {
        if (props.widgetRefresh) {
          props.widgetRefresh();
        }
      })
    }

    const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };
    const requestInitBody = { aggs: {} };
    if (localAggs) {
      requestInitBody.aggs = { [label]: localAggs };
    }
    widgetRequest.requestInit = { body: requestInitBody };
    props.saveWidgetRequests([widgetRequest]);
  }

  const updatedDefaultSettings = {...defaultSettings, color: theme.colors.primary.dark, ...props?.widget?.data?.settings};
  const [settingsState, setSettingsState] = useState<ShareOfVoiceSettingsStateType>(updatedDefaultSettings);
  const settingsOnChange = (updatedState: ShareOfVoiceSettingsStateType) => setSettingsState(updatedState);

  const displayValueOptions = getDisplayValueOptions(i18n);
  const sortingOptions = getCategorySortingOptions(i18n);

  const [tabValue, setTabValue] = useState<number>(0);
  const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newValue: number) => setTabValue(newValue);

  return (
    <TabbedDialog
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateAggs();
        props.setSettingsDialogIsOpen(false);
      }}
      open={true}
      tabs={[i18n.t("widget-settings.query-and-data"), i18n.t("display")]}
      title={i18n.t("widget-types.share-of-voice.settings.title")}>
      <>
        {/* Tab #1 */}
        {tabValue === 0 && (<>
          {props.queryInfo && (<>
            <SimpleFormLabel>{i18n.t("category")}</SimpleFormLabel>
            <LeafAggSelector
              allowedFieldTypes={["decimal", "integer", "keyword", "keywords"]}
              queryInfo={props.queryInfo}
              aggs={localAggs}
              setAggs={(newAggs: Agg | null) => setLocalAggs(newAggs || undefined)}
              aggsLabel={aggsLabel}
              setAggsLabel={setAggsLabel}>
            </LeafAggSelector>
          </>)}
        </>)}

        {/* Tab #2 */}
        {tabValue === 1 && 
        <SplitSettingsWrapper>
          {/* Preview */}
          <ShareOfVoicePreview settingsState={settingsState} />

          {/* Settings Options*/}
          <SettingsOptions onChange={settingsOnChange} settingsState={settingsState}>
            <SettingsAccordion label={i18n.t("widget-settings.widget-title")} showBottomDivider={true}>
              <SettingsWidgetTitleSelect
                keyid="widgetTitle"
                label={i18n.t("title")}
                showQueryOption={true}
                widgetTitleValue={settingsState.widgetTitle} />
                {settingsState.widgetTitle === 'custom' &&
                  <Stack direction="row" spacing={2} sx={{ marginLeft: 2 }}>
                    <Divider flexItem orientation="vertical" sx={{ width: "5px" }} />
                    <SettingsTextField
                      keyid="customWidgetTitle"
                      label={i18n.t("widget-settings.custom-text")}
                      value={settingsState.customWidgetTitle}
                    />
                  </Stack>
                }
              <Box sx={{ mt: 2 }}>
                <SettingsToggleButton
                  keyid="widgetTitleAlignment"
                  label={i18n.t("widget-settings.alignment")}
                  optionsList={horizontalAlignmentOptions}
                  value={settingsState.widgetTitleAlignment} />
              </Box>
            </SettingsAccordion>

            {/* Sorting */}
            <SettingsSingleSelect
              defaultValue={sortingOptions[0]}
              keyid="sortValue"
              label={i18n.t("widgets.settings.sorting.label")}
              options={sortingOptions}
              value={sortingOptions.find(opt => opt.value === settingsState.sortValue) || sortingOptions[1]}
            />

            {/* Display Values */}
            <SettingsSingleSelect
              defaultValue={displayValueOptions[1]}
              keyid="displayValue"
              label={i18n.t("widget-types.share-of-voice.settings.display-values")}
              options={displayValueOptions}
              value={displayValueOptions.find(opt => opt.value === settingsState.displayValue) || displayValueOptions[1]}
            />
          </SettingsOptions>
        </SplitSettingsWrapper> 
        }
      </>
    </TabbedDialog>
  )
}
